import { useAuth } from "@/hooks/useAuth/useAuth";
import { createFileRoute } from "@tanstack/react-router";
import { DefaultContainer } from "@base/components/layout/containers/DefaultContainer";
import { Perfil as ModelClass } from "@/models/Perfil.model";

import { Button, Card, Col, Form, Image, Input, notification, Row, Select, Tag } from "antd";
import { Icon } from "@iconify/react";
import { EditFilled } from "@ant-design/icons";
import { SelectorQuery } from "@base/components/form/SelectorQuery/SelectorQuery";
import { usePagina } from "@base/hooks/usePagina/usePagina";
import { useCallback, useEffect, useMemo, useState } from "react";
import useHttp from "@base/hooks/useHttp/useHttp";
import { useForm } from "antd/es/form/Form";
import SeccionCambiarContrasena from "@/components/SeccionCambiarContrasena/SeccionCambiarContrasena";
import { useQuery } from "@tanstack/react-query";
import { Empresa } from "@/models/Empresa.model";
import { bancos } from "@/constants/bancos";
import VITE_ENV from "@/config/constants/vite-env";
import AcuseButton from "@/components/AcuseButton";
import SentryTestButton from "@/components/SentryTestButton";
import { calcularLlenadoPerfil } from "@/utils/evaluarEmpresaPerfil";

export const Route = createFileRoute("/(auth)/perfil/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { usuario, refrescarUsuario } = useAuth();
  const { navigate } = usePagina();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [confirmado, setConfirmado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = useForm();

  const { get, post } = useHttp();

  const estadoWatcher = Form.useWatch("estado", form);

  const { data: estadosData, isLoading: estadosLoading } = useQuery({
    queryKey: ["proveedor/catalogos/estado.json", get],
    queryFn: () =>
      get({
        endpoint: "proveedor/catalogos/estado.json",
        params: { ordenar: "clave-asc", limite: 32 },
      }),
  });

  const estadoEncontrado = useMemo(() => {
    if (!estadosData || !estadoWatcher) return undefined;
    const lista =
      estadosData.resultado ||
      estadosData.detalle ||
      (Array.isArray(estadosData) ? estadosData : []);
    if (!Array.isArray(lista)) return undefined;
    return lista.find(
      (e: any) =>
        String(e.nombre).toUpperCase() ===
          String(estadoWatcher).toUpperCase() ||
        String(e.id) === String(estadoWatcher),
    );
  }, [estadosData, estadoWatcher]);

  const { data: municipiosData, isLoading: municipiosLoading } = useQuery({
    queryKey: [
      "proveedor/catalogos/municipio.json",
      estadoEncontrado?.id,
      estadoWatcher,
      get,
    ],
    queryFn: () =>
      get({
        endpoint: "proveedor/catalogos/municipio.json",
        params: {
          ordenar: "clave-asc",
          estado: estadoEncontrado?.id ?? undefined,
          limite: 570,
        },
      }),
    enabled: true,
  });

  const estados = useMemo(() => {
    if (!estadosData) return [];
    const data =
      estadosData.resultado ||
      estadosData.detalle ||
      (Array.isArray(estadosData) ? estadosData : []);
    if (!Array.isArray(data)) return [];
    return data.map((e: any) => ({
      label: e.nombre || e.etiqueta || e.estado || e,
      value: e.nombre || e.valor || e.estado || e,
    }));
  }, [estadosData]);

  const municipios = useMemo(() => {
    if (!municipiosData) return [];
    const data =
      municipiosData.resultado ||
      municipiosData.detalle ||
      (Array.isArray(municipiosData) ? municipiosData : []);
    if (!Array.isArray(data)) return [];
    return data.map((m: any) => ({
      label: m.nombre || m.etiqueta || m.municipio || m,
      value: m.nombre || m.valor || m.municipio || m,
    }));
  }, [municipiosData]);

  const handleEditar = () => {
    navigate({
      to: `${ModelClass.BASE_ROUTE}/detalle`,
    });
  };

  const getQr = async () => {
    try {
      await get({
        endpoint: "admin/usuario/generate-qr.json",
        onSuccess: (data) => {
          if (data?.detalle) {
            setQrUrl(data?.detalle);
          }
        },
      });
    } catch (e) {
      return e;
    }
  };

  const postCodigo = async () => {
    setLoading(true);
    const values = await form.validateFields();

    try {
      await post({
        endpoint: "admin/usuario/verify.json",
        body: values,
        onSuccess: () => {
          setConfirmado(true);
        },
      });
    } catch (e) {
      return e;
    } finally {
      setLoading(false);
    }
  };

  const postPwd = async () => {
    const values = await form.validateFields();

    try {
      await post({
        endpoint: "admin/session/change-pwd.json",
        body: values,
        onSuccess: () => {
          form.resetFields();
        },
      });
    } catch (e) {
      return e;
    }
  };

  const desacctivarTotp = async () => {
    try {
      await post({
        endpoint: "admin/usuario/disable-totp.json",
        onSuccess: () => {
          refrescarUsuario();
        },
      });
    } catch (e) {
      return e;
    }
  };

  const qEmpresa = useQuery({
    queryKey: [Empresa.ENDPOINTS.DEFAULT, usuario?.id, get],
    queryFn: () =>
      get({
        endpoint: Empresa.ENDPOINTS.DEFAULT,
        params: {
          expand: "grupos,empresaGrupos,empresaGrupos.grupo,avancePerfil",
        },
      }),
  });

  const rawEmpresa =
    qEmpresa.data?.resultado ?? qEmpresa.data?.detalle ?? qEmpresa.data;
  const empresa = (
    Array.isArray(rawEmpresa) ? rawEmpresa[0] : rawEmpresa
  ) as any;

  const listaGrupos = useMemo(() => {
    if (Array.isArray(empresa?.grupos) && empresa.grupos.length > 0) {
      return empresa.grupos;
    }
    if (
      Array.isArray(empresa?.empresaGrupos) &&
      empresa.empresaGrupos.length > 0
    ) {
      return empresa.empresaGrupos.map(
        (eg: any) => eg.grupo || eg.nombre || eg,
      );
    }
    if (empresa?.grupos && typeof empresa.grupos === "object") {
      return [empresa.grupos];
    }
    if (empresa?.grupo) {
      return Array.isArray(empresa.grupo) ? empresa.grupo : [empresa.grupo];
    }
    return [];
  }, [empresa]);

  const getAcuse = async () => {
    try {
      const params: Record<string, any> = {
        id: empresa?.id,
      };
      window.open(
        `${VITE_ENV.BASE_API_URL}/acuse.pdf?${new URLSearchParams(params).toString()}`,
        "_blank",
      );
    } catch (e) {
      return e;
    }
  };

  const currentEmpresaData = useMemo(() => {
    return {
      ...empresa,
      correoUsuario: empresa?.correoUsuario || usuario?.correoUsuario,
    };
  }, [empresa, usuario]);

  const avancePerfil = useMemo(() => {
    return calcularLlenadoPerfil(currentEmpresaData);
  }, [currentEmpresaData]);

  const colorAvance = useMemo(() => {
    const porcentajeAvance = avancePerfil.porcentaje ?? 0;
    if (porcentajeAvance === 100) {
      return "green";
    }
    if (porcentajeAvance >= 33) {
      return "orange";
    }
    return "red";
  }, [avancePerfil.porcentaje]);

  const grupos = useMemo(() => {
    return listaGrupos
      .map((g: any) =>
        typeof g === "string"
          ? g
          : g?.nombre ||
            g?.etiqueta ||
            g?.nombreGrupo ||
            g?.descripcion ||
            g?.titulo ||
            JSON.stringify(g),
      )
      .filter(Boolean)
      .join(", ");
  }, [listaGrupos]);

  const isFisica = useMemo(() => {
    const tp = String(empresa?.tipoPersona || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return tp.includes("fisic") || tp === "fisica";
  }, [empresa?.tipoPersona]);

  const [guardandoEmpresa, setGuardandoEmpresa] = useState(false);

  useEffect(() => {
    if (empresa) {
      const gruposVal = listaGrupos.map((g: any) =>
        typeof g === "string" ? g : g?.id || g?.valor || g?.nombre,
      );
      form.setFieldsValue({
        grupos: gruposVal,
        nombreComercial: empresa.nombreComercial,
        representante: empresa.representante,
        telefono: empresa.telefono,
        celular: empresa.celular,
        ine: empresa.ine,
        correoUsuario: empresa.correoUsuario || usuario?.correoUsuario,
        calle: empresa.calle,
        numeroExterior: empresa.numeroExterior,
        numeroInterior: empresa.numeroInterior,
        colonia: empresa.colonia,
        ciudad: empresa.ciudad,
        estado: empresa.estado,
        codigoPostal: empresa.codigoPostal,
        referencia: empresa.referencia,
        banco: empresa.banco,
        clabe: empresa.clabe,
        escrituraNumero: empresa.escrituraNumero,
        escrituraVolumen: empresa.escrituraVolumen,
        escrituraFecha: empresa.escrituraFecha
          ? String(empresa.escrituraFecha).split(" ")[0].split("T")[0]
          : undefined,
        notarioNombre: empresa.notarioNombre,
        notarioNumero: empresa.notarioNumero,
        notarioCiudad: empresa.notarioCiudad,
        registroPublicoNumero: empresa.registroPublicoNumero,
        repNoEscritura: empresa.repNoEscritura,
        repVolEscritura: empresa.repVolEscritura,
        repFechaEscritura: empresa.repFechaEscritura
          ? String(empresa.repFechaEscritura).split(" ")[0].split("T")[0]
          : undefined,
        repNombreNotario: empresa.repNombreNotario,
        repNoNotario: empresa.repNoNotario,
        repCiudadNotaria: empresa.repCiudadNotaria,
        repEstadoNotaria: empresa.repEstadoNotaria,
        giro: empresa?.giro,
        idRegimenFiscal: empresa.idRegimenFiscal,
      });
    }
  }, [empresa, form, listaGrupos, usuario]);

  const debounceRegimen = useCallback(
    (v: any) => {
      const _clave = v?._clave ?? v?.clave;
      if (_clave !== "605") {
        return;
      }
      notification.warning({
        message: "Atención",
        description: "Es necesario seleccionar otro régimen",
      });
      form.resetFields(["idRegimenFiscal"]);
      return;
    },
    [form],
  );

const postEmpresa = async () => {
    try {
      setGuardandoEmpresa(true);
      const camposAValidar = [
        "telefono",
        "celular",
        "ine",
        "calle",
        "numeroExterior",
        "numeroInterior",
        "colonia",
        "ciudad",
        "estado",
        "codigoPostal",
        "referencia",
        "banco",
        "clabe",
        "giro",
        "idRegimenFiscal",
      ];

      if (!isFisica) {
        camposAValidar.push(
          "nombreComercial",
          "representante",
          "escrituraNumero",
          "escrituraVolumen",
          "escrituraFecha",
          "notarioNombre",
          "notarioNumero",
          "notarioCiudad",
          "registroPublicoNumero",
          "repNoEscritura",
          "repVolEscritura",
          "repFechaEscritura",
          "repNombreNotario",
          "repNoNotario",
          "repCiudadNotaria",
          "repEstadoNotaria",
        );
      }

      const values = await form.validateFields(camposAValidar);

      await post({
        endpoint: Empresa.ENDPOINTS.DEFAULT,
        body: values,
        onSuccess: () => {
          qEmpresa.refetch();
        },
      });
    } catch (e) {
      return e;
    } finally {
      setGuardandoEmpresa(false);
    }
  };

  return (
    <DefaultContainer className="h-full w-full flex flex-row align-middle justify-center items-center">
      <Card className="h-full w-full md:w-5/6 shadow-sm">
        <Form form={form} layout="vertical">
          <Row gutter={[24, 16]}>
            <div className="mx-auto w-full p-6 flex flex-col gap-6">
              <section className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2 text-2xl font-semibold">
                  <Icon
                    icon="lucide:building-2"
                    className="text-3xl text-primary"
                  />
                  <span>Detalle de Empresa</span>
                </div>
                <div className="flex flex-row gap-1">
                  <AcuseButton id={empresa?.id} validado={empresa?.validado} />
                  <Tag className="text-xl" color={colorAvance}>
                    Avance de Perfil: {avancePerfil.porcentaje}% (
                    {avancePerfil.completados}/
                    {avancePerfil.totales})
                  </Tag>
                </div>
              </section>

              {/* Datos generales */}
              <section className="flex flex-col gap-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                  <Icon icon="lucide:info" className="text-xl" /> Datos
                  generales
                </div>

                <Row gutter={[24, 16]}>
                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        Grupo(s)
                      </span>
                      {listaGrupos.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {listaGrupos.map((g: any, i: number) => {
                            const label =
                              typeof g === "string"
                                ? g
                                : g?.nombre ||
                                  g?.etiqueta ||
                                  g?.nombreGrupo ||
                                  g?.descripcion ||
                                  g?.titulo ||
                                  String(g);
                            return (
                              <Tag
                                key={i}
                                color="blue"
                                className="text-sm py-1 px-2 font-normal"
                              >
                                {label}
                              </Tag>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-base text-gray-900 font-normal">
                          {empresa?.idGrupo || "-"}
                        </span>
                      )}
                    </div>
                  </Col>

                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        <span className="text-red-500 mr-1">*</span>Tipo de
                        persona
                      </span>
                      <span className="text-base text-gray-900 font-normal">
                        {empresa?.tipoPersona || "-"}
                      </span>
                    </div>
                  </Col>

                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        <span className="text-red-500 mr-1">*</span>Nombre /
                        Razón Social
                      </span>
                      <span className="text-base text-gray-900 font-normal">
                        {empresa?.razonSocial ||
                          empresa?.nombre ||
                          usuario?.razonSocial ||
                          usuario?.nombre ||
                          "-"}
                      </span>
                    </div>
                  </Col>

                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        RFC
                      </span>
                      <span className="text-base text-gray-900 font-normal">
                        {empresa?.rfc || "-"}
                      </span>
                    </div>
                  </Col>

                  {!isFisica && (
                    <Col span={24} md={12}>
                      <Form.Item
                        label="Nombre Comercial"
                        name="nombreComercial"
                        className="mb-0"
                      >
                        <Input placeholder="Nombre Comercial" />
                      </Form.Item>
                    </Col>
                  )}

                  {!isFisica && (
                    <Col span={24} md={12}>
                      <Form.Item
                        label="Representante"
                        name="representante"
                        className="mb-0"
                      >
                        <Input placeholder="Nombre del representante" />
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={24} md={12}>
                    <Form.Item
                      label="Teléfono"
                      name="telefono"
                      className="mb-0"
                    >
                      <Input placeholder="Teléfono" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={12}>
                    <Form.Item
                      label={
                        <span>
                          <span className="text-red-500 mr-1">*</span>Celular
                        </span>
                      }
                      name="celular"
                      className="mb-0"
                      rules={[
                        {
                          required: true,
                          message: "El celular es obligatorio",
                        },
                      ]}
                    >
                      <Input placeholder="Celular" />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={12}>
                    <Form.Item
                      label="INE (IDMX)"
                      name="ine"
                      className="mb-0"
                      tooltip={<Image src="/img/ine.jpg" />}
                      rules={[
                        {
                          min: 10,
                          message: "Debe tener al menos 10 caracteres",
                        },
                      ]}
                    >
                      <Input
                        placeholder="10 Digitos del IDMX de su CREDENCIAL INE"
                        maxLength={10}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={12}>
                    <Form.Item label="Giro" name="giro" className="mb-0">
                      <Input placeholder="Giro al que pertenece" />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={12}>
                    <Form.Item
                      label="Régimen Fiscal"
                      name="idRegimenFiscal"
                      className="mb-0"
                      rules={[
                        {
                          required: true,
                          message:
                            "Por favor seleccionar un Régimen Fiscal.",
                        },
                      ]}
                    >
                      <SelectorQuery
                        queryProps={{
                          endpoint: "proveedor/catalogos/regimen-fiscal.json",
                          enabled: true,
                          extraParams: {
                            ordenar: "clave-asc",
                          },
                        }}
                        onSelect={debounceRegimen}
                        selectProps={{
                          placeholder: "Seleccione...",
                          allowClear: true,
                        }}
                        labelRender={(data) =>
                          `${data?.data?.clave} - ${data?.data?.nombre?.toUpperCase()}`
                        }
                        optionRender={(data) =>
                          `${data?.data?.clave} - ${data?.data?.nombre?.toUpperCase()}`
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        Correo (Usuario)
                      </span>
                      <span className="text-base text-gray-900 font-normal">
                        {empresa?.correoUsuario ||
                          usuario?.correoUsuario ||
                          "-"}
                      </span>
                    </div>
                  </Col>
                  <Col span={24} md={12}>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-600">
                        Correo (Notificación)
                      </span>
                      <Form.Item name="correoNotificacion" className="mb-0">
                        <Input placeholder="Correo de notificaciones" />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </section>

              <section className="flex flex-col gap-4 border-t pt-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                  <Icon icon="lucide:map-pin" className="text-xl" /> Dirección
                </div>

                <Row gutter={[24, 16]}>
                  <Col span={24} md={8}>
                    <Form.Item label="Calle" name="calle" className="mb-0">
                      <Input placeholder="Calle" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item
                      label="Número Exterior"
                      name="numeroExterior"
                      className="mb-0"
                    >
                      <Input placeholder="Número Exterior" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item
                      label="Número Interior"
                      name="numeroInterior"
                      className="mb-0"
                    >
                      <Input placeholder="Número Interior" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item label="Colonia" name="colonia" className="mb-0">
                      <Input placeholder="Colonia" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item label="Estado" name="estado" className="mb-0">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Seleccionar estado"
                        loading={estadosLoading}
                        options={estados}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onChange={() => {
                          form.setFieldValue("ciudad", undefined);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item label="Ciudad" name="ciudad" className="mb-0">
                      <Select
                        showSearch
                        allowClear
                        placeholder={
                          estadoWatcher
                            ? "Seleccionar ciudad"
                            : "Seleccionar estado primero"
                        }
                        loading={municipiosLoading}
                        options={municipios}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item
                      label="Código Postal"
                      name="codigoPostal"
                      className="mb-0"
                    >
                      <Input placeholder="Código Postal" />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <Form.Item
                      label="Referencia"
                      name="referencia"
                      className="mb-0"
                    >
                      <Input placeholder="Referencia" />
                    </Form.Item>
                  </Col>
                </Row>
              </section>

              <section className="flex flex-col gap-4 border-t pt-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                  <Icon icon="lucide:landmark" className="text-xl" /> Datos
                  bancarios
                </div>

                <Row gutter={[24, 16]}>
                  <Col span={24} md={12}>
                    <Form.Item label="Banco" name="banco" className="mb-0">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Seleccionar banco"
                        options={bancos}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={12}>
                    <Form.Item
                      label="CLABE"
                      name="clabe"
                      className="mb-0"
                      getValueFromEvent={(e) =>
                        e.target.value.replace(/\D/g, "")
                      }
                      rules={[
                        {
                          len: 18,
                          message: "La CLABE debe tener exactamente 18 dígitos",
                        },
                      ]}
                    >
                      <Input placeholder="CLABE interbancaria" maxLength={18} />
                    </Form.Item>
                  </Col>
                </Row>
              </section>

              {!isFisica && (
                <section className="flex flex-col gap-4 border-t pt-4">
                  <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                    <Icon icon="lucide:file-text" className="text-xl" /> Datos
                    de Escritura / Acta Constitutiva
                  </div>

                  <Row gutter={[24, 16]}>
                    <Col span={24} md={8}>
                      <Form.Item
                        label="Número de Escritura"
                        name="escrituraNumero"
                        className="mb-0"
                      >
                        <Input placeholder="Número de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Volumen de Escritura"
                        name="escrituraVolumen"
                        className="mb-0"
                      >
                        <Input placeholder="Volumen de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Fecha de Escritura"
                        name="escrituraFecha"
                        className="mb-0"
                      >
                        <Input type="date" placeholder="Fecha de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={12}>
                      <Form.Item
                        label="Nombre del Notario"
                        name="notarioNombre"
                        className="mb-0"
                      >
                        <Input placeholder="Nombre del notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={6}>
                      <Form.Item
                        label="Número de Notario"
                        name="notarioNumero"
                        className="mb-0"
                      >
                        <Input placeholder="Número de notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={6}>
                      <Form.Item
                        label="Ciudad del Notario"
                        name="notarioCiudad"
                        className="mb-0"
                      >
                        <Input placeholder="Ciudad del notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={12}>
                      <Form.Item
                        label="Número de Registro Público"
                        name="registroPublicoNumero"
                        className="mb-0"
                      >
                        <Input placeholder="Número de registro público" />
                      </Form.Item>
                    </Col>
                  </Row>
                </section>
              )}

              {!isFisica && (
                <section className="flex flex-col gap-4 border-t pt-4">
                  <div>
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                      <Icon icon="lucide:user-square" className="text-xl" />{" "}
                      Datos de escritura del Representante Legal
                    </div>
                    <p className="text-sm text-gray-500 m-0 mt-1">
                      (En caso que sean los mismos de la escritura, repetirlos o si es otra acta la del poder, proporcionarlos)
                    </p>
                  </div>

                  <Row gutter={[5, 5]}>
                    <Col span={24} md={8}>
                      <Form.Item
                        label="Número de Escritura"
                        name="repNoEscritura"
                        className="mb-0"
                      >
                        <Input placeholder="Número de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Volumen de Escritura"
                        name="repVolEscritura"
                        className="mb-0"
                      >
                        <Input placeholder="Volumen de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Fecha de Escritura"
                        name="repFechaEscritura"
                        className="mb-0"
                      >
                        <Input type="date" placeholder="Fecha de escritura" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Nombre del Notario"
                        name="repNombreNotario"
                        className="mb-0"
                      >
                        <Input placeholder="Nombre del notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Número de Notario"
                        name="repNoNotario"
                        className="mb-0"
                      >
                        <Input placeholder="Número de notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Ciudad del Notario"
                        name="repCiudadNotaria"
                        className="mb-0"
                      >
                        <Input placeholder="Ciudad del notario" />
                      </Form.Item>
                    </Col>

                    <Col span={24} md={8}>
                      <Form.Item
                        label="Estado de la Notaría"
                        name="repEstadoNotaria"
                        className="mb-0"
                      >
                        <Input placeholder="Estado de la notaría" />
                      </Form.Item>
                    </Col>
                  </Row>
                </section>
              )}

              <section className="flex flex-col gap-4 border-t pt-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                  <Icon icon="lucide:lock" className="text-xl" /> Cambiar
                  Contraseña
                </div>

                <Row gutter={[10, 16]}>
                  <Col span={24}>
                    <Row>
                      <SeccionCambiarContrasena
                        formName="mi-perfil"
                        required={false}
                      />
                    </Row>
                  </Col>
                  <Col span={24} className="flex justify-start">
                    <Button onClick={postPwd} type="primary">
                      Cambiar Password
                    </Button>
                  </Col>
                </Row>
              </section>

              <div className="flex justify-end border-t pt-4 mt-2">
                <Button
                  type="primary"
                  size="large"
                  icon={<EditFilled />}
                  loading={guardandoEmpresa}
                  onClick={postEmpresa}
                  className="px-8"
                >
                  Guardar Cambios
                </Button>
              </div>
            </div>
          </Row>
        </Form>
      </Card>
    </DefaultContainer>
  );
}
